import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { UserResetPasswordModel } from 'common/data/user/reset-password/model';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { GoogleRecaptchaViewStore } from 'common/module/google/recaptcha/view-store';
import { GoogleRecaptchaViewStoreEvent } from 'common/module/google/recaptcha/view-store.event';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { KeyboardCharcodeEnum } from 'common/module/keyboard/charcode.enum';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { UserForgotPasswordFieldErrorsInterface } from 'common/module/user/forgot-password/field-errors.interface';
import { UserForgotPasswordFieldsInterface } from 'common/module/user/forgot-password/fields.interface';
import { UserForgotPasswordStatechart } from 'common/module/user/forgot-password/statechart';
import { UserForgotPasswordStatechartEvent } from 'common/module/user/forgot-password/statechart.event';
import { UserForgotPasswordStatechartActionEnum } from 'common/module/user/forgot-password/statechart/action.enum';
import { UserForgotPasswordTemplatePropsInterface } from 'common/module/user/forgot-password/template-props.interface';
import { UserForgotPasswordViewStoreEvent } from 'common/module/user/forgot-password/view-store.event';
import { UserViewStoreInterface } from 'common/module/user/view-store.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

export class UserForgotPasswordViewStore extends DataStore implements UserViewStoreInterface {
  /**
   * @inheritDoc
   */
  protected state: UserForgotPasswordTemplatePropsInterface;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private captcha: GoogleRecaptchaViewStore,
    private statechartStore: StatechartStoreInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initialize(): void {
    this.state = {
      error: '',
      success: false,
      fields: new UserResetPasswordModel(),
      fieldErrors: {
        email: '',
        captcha_token: '',
      },
      loaderEnabled: false,
      captcha: this.captcha.getState(),
      onKeyUpInput: this.onKeyUpInput,
      onChangeInput: this.onChangeInput,
      onClickResetPassword: this.onClickResetPassword,
      onClose: this.onClose,
      onClickCreateAccount: this.onClickCreateAccount,
    };

    this.statechartStore.initialize({
      statechart: UserForgotPasswordStatechart,
    });

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public updateLoaderEnabled(enabled: boolean): void {
    this.setState(
      {
        ...this.state,
        loaderEnabled: enabled,
      },
      this.updateLoaderEnabled
    );
  }

  /**
   * Form validators
   */
  protected getValidators(): {
    [key: string]: InputValidatorInterface[];
  } {
    return {
      email: [
        new InputValidatorRequired(i18nTranslate('Please enter your email')),
        new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
      ],
      captcha_token: [],
    };
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetFailed, this.onAskPasswordResetFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetValidationFailed, this.onAskPasswordResetValidationFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.askPasswordResetSucceeded, this.onAskPasswordResetSucceeded);

    this.captcha.getEventEmitter().addListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptcha);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserForgotPasswordStatechartActionEnum.validateInput, this.onValidateInputActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserForgotPasswordStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserForgotPasswordStatechartActionEnum.showLoader, this.onShowLoaderActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserForgotPasswordStatechartActionEnum.resetPassword, this.onResetPasswordActionStatechart);
  }

  /**
   * Validate form fields
   */
  private validateFields(): boolean {
    let isValid = true;

    const validators = this.getValidators();

    Object.keys(validators).forEach((fieldName: keyof UserForgotPasswordFieldErrorsInterface) => {
      const errors: string[] = validators[fieldName]
        .map((validator: InputValidatorInterface) => validator.getError(this.state.fields[fieldName], null))
        .filter((message: string) => !!message);

      // Only keep first error if any
      this.state.fieldErrors[fieldName] = errors[0] || '';

      isValid = isValid && !this.state.fieldErrors[fieldName];
    });

    this.setState(this.state);

    return isValid;
  }

  /**
   * Ask password reset
   */
  private askPasswordReset(): void {
    this.statechartStore.transit({
      event: UserForgotPasswordStatechartEvent.wantToResetPassword,
      payload: null,
    });
  }

  /**
   * @inheritDoc
   */
  private onKeyUpInput = (e: KeyboardEvent): void => {
    if (e.keyCode !== KeyboardCharcodeEnum.enter) {
      return;
    }

    this.askPasswordReset();
  };

  /**
   * Change input value
   */
  private onChangeInput = (e: Event, fieldName: keyof UserForgotPasswordFieldsInterface): void => {
    this.state.fields[fieldName] = (<HTMLInputElement>e.target).value;
    this.setState(this.state);
  };

  /**
   * Click close button
   */
  private onClose = (): void => {
    this.getEventEmitter().emit(UserForgotPasswordViewStoreEvent.clickClose);
  };

  /**
   * @inheritDoc
   */
  private onClickResetPassword = () => {
    this.askPasswordReset();
  };

  /**
   * Captcha value has changed
   */
  private onChangeCaptcha = () => {
    this.state.fields.captcha_token = this.captcha.getState().value;

    this.setState(this.state);

    this.statechartStore.transit({
      event: UserForgotPasswordStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Ask for password reset failed
   */
  private onAskPasswordResetFailed = (reason: string) => {
    this.state.error = reason;
    this.state.success = false;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserForgotPasswordStatechartEvent.resetPasswordFailed,
      payload: null,
    });
  };

  /**
   * Ask for password reset validation failed
   */
  private onAskPasswordResetValidationFailed = (errors: UserForgotPasswordFieldErrorsInterface) => {
    if (errors.captcha_token && errors.captcha_token.length) {
      this.captcha.reset();
    }

    this.state.fieldErrors = errors;
    this.state.success = false;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserForgotPasswordStatechartEvent.resetPasswordFailed,
      payload: null,
    });
  };

  /**
   * Ask for password reset succeeded
   */
  private onAskPasswordResetSucceeded = () => {
    this.state.success = true;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserForgotPasswordStatechartEvent.resetPasswordSucceeded,
      payload: null,
    });
  };

  /**
   * Validate input
   */
  private onValidateInputActionStatechart = () => {
    const isValid = this.validateFields();

    if (isValid) {
      this.statechartStore.transit({
        event: UserForgotPasswordStatechartEvent.inputIsValid,
        payload: null,
      });

      return;
    }

    this.statechartStore.transit({
      event: UserForgotPasswordStatechartEvent.inputIsNotValid,
      payload: null,
    });
  };

  /**
   * Show loader
   */
  private onShowLoaderActionStatechart = () => {
    this.updateLoaderEnabled(true);
  };

  /**
   * Execute captcha
   */
  private onExecuteCaptchaActionStatechart = () => {
    if (this.state.fields.captcha_token) {
      this.statechartStore.transit({
        event: UserForgotPasswordStatechartEvent.captchaValueChanged,
        payload: null,
      });

      return;
    }

    this.captcha.execute();
  };

  /**
   * Reset password
   */
  private onResetPasswordActionStatechart = () => {
    this.userAuthenticationService.askPasswordReset(this.state.fields);
  };

  /**
   * @inheritDoc
   */
  private onClickCreateAccount = () => {
    this.getEventEmitter().emit(UserForgotPasswordViewStoreEvent.clickCreateAccount);
  };
}
